import {ReactNode} from 'react';
import Box from '@mui/material/Box';
import {SxProps, Theme} from '@mui/material/styles';
import Card from './Card';
import {kit} from './tokens';

export interface ActionRow {
  icon?: ReactNode;
  label: string;
  onClick?: () => void;
  // Renders in the danger (kit.danger) color, e.g. "Cancel project".
  danger?: boolean;
  // Optional machine-readable tag so other parts of a screen can locate a specific row
  // (e.g. "the one that requests a site visit") without string-matching its label.
  // Purely for lookup -- ActionRowsCard itself ignores it.
  kind?: string;
}

interface ActionRowsCardProps {
  rows: ActionRow[];
  sx?: SxProps<Theme>;
}

// Quiet stacked action rows (icon + label, hairline dividers, optional
// danger row) -- web port of `.card.manage` / `.mrow` in
// web-detail-1-quotes.html. Used for "Edit project / Share / Invite more
// pros / Cancel project"-shaped rail menus.
const ActionRowsCard = ({rows, sx}: ActionRowsCardProps) => (
  <Card sx={[{padding: '6px 0'}, ...(Array.isArray(sx) ? sx : [sx])]}>
    {rows.map((row, index) => (
      <Box
        key={row.label}
        component={row.onClick ? 'button' : 'div'}
        type={row.onClick ? 'button' : undefined}
        onClick={row.onClick}
        sx={{
          width: '100%',
          display: 'flex',
          alignItems: 'center',
          gap: '12px',
          padding: '13px 20px',
          fontFamily: 'inherit',
          fontSize: 14,
          fontWeight: 600,
          textAlign: 'left',
          color: row.danger ? kit.danger : kit.inkSoft,
          backgroundColor: 'transparent',
          border: 'none',
          borderTop: index === 0 ? 'none' : `1px solid ${kit.mist}`,
          cursor: row.onClick ? 'pointer' : 'default',
          '&:hover': row.onClick ? {backgroundColor: kit.mist} : undefined,
        }}>
        {row.icon ? (
          <Box
            sx={{
              color: row.danger ? kit.danger : kit.slate,
              display: 'flex',
              flexShrink: 0,
            }}>
            {row.icon}
          </Box>
        ) : null}
        {row.label}
      </Box>
    ))}
  </Card>
);

export default ActionRowsCard;
